import { useState } from 'react';
import api from '../../services/api';

const EmbeddedFamilyJoin = ({ onComplete, onBack, isLoading, setIsLoading }) => {
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [familyInfo, setFamilyInfo] = useState(null);

  const handleJoinFamily = async () => {
    if (!inviteCode.trim()) {
      setError('Please enter an invite code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await api.post('/families/join', {
        inviteCode: inviteCode.trim()
      });

      if (response.data.success) {
        setFamilyInfo(response.data.family);
        setSuccess(true);
        // Wait a moment to show success message, then complete
        setTimeout(() => {
          onComplete({ familyId: response.data.family.id });
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to join family:', error);
      setError(error.response?.data?.message || 'Failed to join family. Please check your invite code and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success && familyInfo) {
    return (
      <div className="family-success">
        <div className="success-icon">✅</div>
        <h3>Successfully Joined Family!</h3>
        <p>You are now a member of "{familyInfo.name}"</p>
        <div className="family-info">
          <p><strong>Family Name:</strong> {familyInfo.name}</p>
          {familyInfo.description && <p><strong>Description:</strong> {familyInfo.description}</p>}
          <p><strong>Members:</strong> {familyInfo.memberCount || 'Unknown'} members</p>
        </div>
      </div>
    );
  }

  return (
    <div className="embedded-family-join">
      <div className="join-header">
        <div className="join-icon">🤝</div>
        <h3>Join a Family</h3>
        <p>Enter the invite code from a family member to join their group</p>
      </div>

      <div className="join-form">
        <div className="form-group">
          <label htmlFor="inviteCode">Invite Code *</label>
          <input
            type="text"
            id="inviteCode"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            placeholder="Enter invite code (e.g., ABC123)"
            className="form-input"
            disabled={isLoading}
            maxLength="10"
          />
          <small className="form-help">
            The invite code should be provided by a family member or administrator
          </small>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onBack}
            disabled={isLoading}
          >
            Back
          </button>
          <button
            className="btn-primary"
            onClick={handleJoinFamily}
            disabled={isLoading || !inviteCode.trim()}
          >
            {isLoading ? 'Joining...' : 'Join Family'}
          </button>
        </div>
      </div>

      <div className="join-help">
        <h4>Need an invite code?</h4>
        <p>Ask a family member or church administrator for the invite code. The code is usually 6-10 characters long and may contain letters and numbers.</p>
      </div>

      <style jsx>{`
        .embedded-family-join {
          max-width: 500px;
          margin: 0 auto;
        }

        .join-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .join-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .join-header h3 {
          margin: 0 0 8px 0;
          color: #1f2937;
          font-size: 20px;
        }

        .join-header p {
          margin: 0;
          color: #6b7280;
          font-size: 14px;
        }

        .join-form {
          background: #f9fafb;
          padding: 24px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          margin-bottom: 24px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #374151;
          font-size: 14px;
        }

        .form-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 16px;
          font-family: monospace;
          letter-spacing: 1px;
          transition: border-color 0.2s ease;
          box-sizing: border-box;
          text-transform: uppercase;
        }

        .form-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .form-input:disabled {
          background: #f3f4f6;
          cursor: not-allowed;
        }

        .form-help {
          display: block;
          margin-top: 6px;
          font-size: 12px;
          color: #6b7280;
        }

        .error-message {
          background: #fef2f2;
          color: #dc2626;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 14px;
          border: 1px solid #fecaca;
        }

        .form-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .join-help {
          background: #eff6ff;
          padding: 20px;
          border-radius: 12px;
          border: 1px solid #bfdbfe;
        }

        .join-help h4 {
          margin: 0 0 8px 0;
          color: #1e40af;
          font-size: 16px;
        }

        .join-help p {
          margin: 0;
          color: #374151;
          font-size: 14px;
          line-height: 1.5;
        }

        .family-success {
          text-align: center;
          padding: 40px 20px;
        }

        .success-icon {
          font-size: 64px;
          margin-bottom: 16px;
        }

        .family-success h3 {
          margin: 0 0 16px 0;
          color: #059669;
          font-size: 20px;
        }

        .family-success p {
          margin-bottom: 24px;
          color: #6b7280;
        }

        .family-info {
          background: #f0fdf4;
          padding: 16px;
          border-radius: 8px;
          border: 1px solid #bbf7d0;
          text-align: left;
        }

        .family-info p {
          margin: 0 0 8px 0;
          font-size: 14px;
        }

        .family-info p:last-child {
          margin-bottom: 0;
        }

        @media (max-width: 480px) {
          .join-form {
            padding: 20px;
          }

          .form-actions {
            flex-direction: column;
          }

          .btn-primary,
          .btn-secondary {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default EmbeddedFamilyJoin;
